import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { T } from '@design/index';
import { patientApi } from '../../api';
import type { MarketplaceProvider, ProviderPricing, ProviderCertificate } from '../../types';
import { onHospitalOpdConfirmed } from './bookingUtils';
import { preferredTimeGrid, formatSlotTime } from './useProviderSlots';

// ─── Icons ───────────────────────────────────────────────────────────────────

function IconBack({ color }: { color: string }) {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
    </svg>
  );
}

function IconStar({ color }: { color: string }) {
  return (
    <svg width={13} height={13} viewBox="0 0 24 24" fill={color} stroke={color} strokeWidth="1.5">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}

function IconPhone({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27l3-.01a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 9a16 16 0 0 0 6.29 6.29l1.48-1.48a2 2 0 0 1 2.09-.44c.91.35 1.87.59 2.84.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

function IconMail({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2" /><path d="M2 8l10 7 10-7" />
    </svg>
  );
}

function IconClock({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
    </svg>
  );
}

function IconLocation({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function IconShield({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2l8 4v6c0 5-3.5 9.7-8 11-4.5-1.3-8-6-8-11V6l8-4z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

function IconCheck({ color }: { color: string }) {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function parsePricing(raw: ProviderPricing[] | string | undefined): ProviderPricing[] {
  if (!raw) return [];
  if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return []; } }
  return raw;
}

function parseCertificates(raw: ProviderCertificate[] | string | undefined): ProviderCertificate[] {
  if (!raw) return [];
  if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return []; } }
  return raw;
}

// ─── Section header ───────────────────────────────────────────────────────────

function SectionHeader({ title }: { title: string }) {
  return (
    <h2
      style={{
        fontFamily: T.font,
        fontSize: 15,
        fontWeight: 800,
        color: T.text,
        margin: '0 0 12px',
      }}
    >
      {title}
    </h2>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function HospitalProfile() {
  const navigate = useNavigate();
  const { hospitalId } = useParams<{ hospitalId: string }>();

  const [provider, setProvider] = useState<MarketplaceProvider | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Booking state — the `/hospitals/:slug/book` deep link (Discovery's "Book
  // OPD" button) opens the form directly instead of landing on the profile.
  const [showBooking, setShowBooking] = useState(() =>
    typeof window !== 'undefined' && window.location.pathname.endsWith('/book'));
  const [department, setDepartment] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState('');
  const [reason, setReason] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  const [booking, setBooking] = useState(false);
  const [bookError, setBookError] = useState<string | null>(null);
  const [bookResult, setBookResult] = useState<{ tokenNumber?: string; id?: string } | null>(null);

  // Generate next 5 days for date picker
  const datePicker = useMemo(() => {
    const days: { label: string; value: string }[] = [];
    for (let i = 0; i < 5; i++) {
      const d = new Date();
      d.setDate(d.getDate() + i);
      const value = d.toISOString().slice(0, 10);
      const label = d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
      days.push({ label, value });
    }
    return days;
  }, []);

  // The times a patient may ask for. A hospital publishes no per-slot
  // availability here, so these are a stated PREFERENCE (the label says so) —
  // marking some of them "unavailable" would be inventing knowledge.
  const timeSlots = useMemo(() => (selectedDate ? preferredTimeGrid() : []), [selectedDate]);

  useEffect(() => {
    let alive = true;
    if (!hospitalId) return;
    patientApi
      .getMarketplaceProvider(hospitalId)
      .then((data) => {
        if (alive) {
          setProvider(data);
          if (data.specialties?.length) setDepartment(data.specialties[0]);
        }
      })
      .catch(() => {
        if (alive) setError('Unable to load hospital details.');
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [hospitalId]);

  const handleBookOPD = async () => {
    if (!provider) return;
    if (!department) { setBookError('Please select a department.'); return; }
    if (!selectedDate) { setBookError('Please select a date.'); return; }
    if (!selectedSlot) { setBookError('Please select a time slot.'); return; }
    if (!consentChecked) { setBookError('Please accept the consent to continue.'); return; }
    setBooking(true);
    setBookError(null);
    try {
      const appointedAt = `${selectedDate}T${selectedSlot}`;
      const result = await patientApi.bookDoctor({
        providerId: provider.id,
        appointedAt,
        consultationType: 'CLINIC',
        reasonForVisit: `Department: ${department}. ${reason || ''}`.trim(),
      });
      onHospitalOpdConfirmed({
        id: result?.id,
        doctorName: 'OPD Doctor',
        specialty: department || 'General',
        hospital: provider.name,
        date: selectedDate,
        time: selectedSlot,
        notes: `Department: ${department}. Reason: ${reason}`,
      });
      // The consent the patient just accepted becomes a REAL grant on their
      // account — checked required above, so record it; failure is surfaced.
      try {
        // Binds by card id; a typed name binds to nobody.
        if (!provider.neoHealthCardId) throw new Error('no card id');
        await patientApi.createConsent({
          sharedWithName: provider.name,
          sharedWithRole: 'HOSPITAL',
          sharedWithNhcId: provider.neoHealthCardId,
          scopes: ['MEDICAL_HISTORY'],
          validHours: 72,
        });
      } catch {
        setBookError('Booking succeeded, but the consent grant could not be recorded — grant it from Consent & Sharing.');
      }
      setBookResult({ tokenNumber: result.tokenNumber, id: result.id });
    } catch (err: any) {
      setBookError(err?.message || 'Could not complete booking. Please try again.');
    } finally {
      setBooking(false);
    }
  };

  if (loading) {
    return (
      <div style={{ background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', paddingBottom: 100 }}>
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              border: `3px solid ${T.border}`,
              borderTopColor: '#DC2626',
              margin: '0 auto 12px',
              animation: 'spin 0.8s linear infinite',
            }}
          />
          <p style={{ fontFamily: T.font, fontSize: 13, color: T.textSec }}>Loading hospital...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (error || !provider) {
    return (
      <div style={{ background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, paddingBottom: 100 }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontFamily: T.font, fontSize: 14, color: '#B91C1C', marginBottom: 16 }}>{error ?? 'Hospital not found.'}</p>
          <button
            onClick={() => navigate('/patient/care-services/hospitals')}
            style={{ fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.white, background: '#DC2626', border: 'none', borderRadius: 10, padding: '10px 20px', cursor: 'pointer' }}
          >
            Back to Hospitals
          </button>
        </div>
      </div>
    );
  }

  const pricing = parsePricing(provider.pricing);
  const certificates = parseCertificates(provider.certificates);

  return (
    <div style={{ background: T.bg, fontFamily: T.font, maxWidth: 1080, margin: '0 auto', paddingBottom: 100 }}>
      {/* Hero */}
      <div
        style={{
          background: `linear-gradient(135deg, #DC2626 0%, #991B1B 100%)`,
          padding: '20px 16px 32px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'absolute', top: -30, right: -30, width: 130, height: 130, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />

        {/* Back + Share */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <button
            onClick={() => navigate('/patient/care-services/hospitals')}
            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
          >
            <IconBack color={T.white} />
            <span style={{ fontFamily: T.font, fontSize: 11, fontWeight: 600, color: T.white }}>Back</span>
          </button>
          <button
            onClick={() => {
              const text = `${provider.name}\n${provider.address || ''}${provider.city ? `, ${provider.city}` : ''}\nRating: ${provider.rating}/5\n${provider.specialties?.length ? `Departments: ${provider.specialties.join(', ')}` : ''}\n\nViewed on NeoHealthCard`;
              if (navigator.share) { navigator.share({ title: provider.name, text }).catch(() => {}); }
              else { navigator.clipboard.writeText(text).then(() => alert('Hospital details copied to clipboard')).catch(() => {}); }
            }}
            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
          >
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={T.white} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="M8.59 13.51l6.83 3.98"/><path d="M15.41 6.51l-6.82 3.98"/>
            </svg>
            <span style={{ fontFamily: T.font, fontSize: 11, fontWeight: 600, color: T.white }}>Share</span>
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              background: 'rgba(255,255,255,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke={T.white} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 21h18" /><path d="M5 21V7l7-4 7 4v14" />
              <rect x="9" y="10" width="6" height="6" />
              <path d="M12 10v6" /><path d="M9 13h6" />
            </svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ fontFamily: T.font, fontSize: 18, fontWeight: 800, color: T.white, margin: '0 0 4px', lineHeight: 1.3 }}>
              {provider.name}
            </h1>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {provider.isVerified && (
                <span style={{ fontFamily: T.font, fontSize: 10, fontWeight: 700, color: T.white, background: 'rgba(255,255,255,0.2)', borderRadius: 20, padding: '2px 9px' }}>
                  ✓ NHC Verified
                </span>
              )}
              {provider.city && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: T.font, fontSize: 11, color: 'rgba(255,255,255,0.8)' }}>
                  <IconLocation color="rgba(255,255,255,0.6)" />{provider.city}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Rating */}
        {provider.rating > 0 && (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '4px 10px' }}>
            <IconStar color="#FCD34D" />
            <span style={{ fontFamily: T.font, fontSize: 12, fontWeight: 700, color: T.white }}>
              {provider.rating.toFixed(1)} · {provider.reviewCount} reviews
            </span>
          </div>
        )}
      </div>

      {/* Address & contact */}
      <div style={{ padding: '0 16px', marginTop: -16, position: 'relative', zIndex: 5 }}>
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: '14px 16px', boxShadow: T.shadow }}>
          {provider.address && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10 }}>
              <div style={{ marginTop: 1, flexShrink: 0 }}><IconLocation color={T.textTer} /></div>
              <span style={{ fontFamily: T.font, fontSize: 12, color: T.textSec, lineHeight: 1.5 }}>
                {provider.address}{provider.city ? `, ${provider.city}` : ''}{provider.state ? `, ${provider.state}` : ''}
              </span>
            </div>
          )}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {provider.phone && (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <IconPhone color={T.textTer} />
                <a href={`tel:${provider.phone}`} style={{ fontFamily: T.font, fontSize: 12, color: '#DC2626', textDecoration: 'none', fontWeight: 600 }}>
                  {provider.phone}
                </a>
              </div>
            )}
            {provider.email && (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <IconMail color={T.textTer} />
                <a href={`mailto:${provider.email}`} style={{ fontFamily: T.font, fontSize: 12, color: '#DC2626', textDecoration: 'none', fontWeight: 600 }}>
                  {provider.email}
                </a>
              </div>
            )}
            {provider.openHours && (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <IconClock color={T.textTer} />
                <span style={{ fontFamily: T.font, fontSize: 12, color: T.textSec }}>{provider.openHours}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Departments */}
      {provider.specialties && provider.specialties.length > 0 && (
        <div style={{ padding: '20px 16px 0' }}>
          <SectionHeader title="Departments" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {provider.specialties.map((spec) => (
              <div
                key={spec}
                style={{
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: 12,
                  padding: '12px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: '#DC2626',
                    flexShrink: 0,
                  }}
                />
                <span style={{ fontFamily: T.font, fontSize: 12, fontWeight: 600, color: T.text }}>
                  {spec}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pricing */}
      {pricing.length > 0 && (
        <div style={{ padding: '20px 16px 0' }}>
          <SectionHeader title="Services & Pricing" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pricing.map((item, i) => (
              <div
                key={i}
                style={{
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: 12,
                  padding: '12px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <p style={{ fontFamily: T.font, fontSize: 13, fontWeight: 600, color: T.text, margin: '0 0 2px' }}>{item.service}</p>
                  {item.note && <p style={{ fontFamily: T.font, fontSize: 11, color: T.textSec, margin: 0 }}>{item.note}</p>}
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <span style={{ fontFamily: T.font, fontSize: 14, fontWeight: 700, color: '#DC2626' }}>₹{item.price}</span>
                  {item.unit && <span style={{ fontFamily: T.font, fontSize: 10, color: T.textSec, marginLeft: 2 }}>/{item.unit}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tags as facility badges */}
      {provider.tags.length > 0 && (
        <div style={{ padding: '20px 16px 0' }}>
          <SectionHeader title="Facilities" />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {provider.tags.map((tag) => (
              <span
                key={tag}
                style={{
                  fontFamily: T.font,
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#DC2626',
                  background: '#DC262610',
                  border: '1px solid #DC262620',
                  borderRadius: 20,
                  padding: '5px 12px',
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Certificates */}
      {certificates.length > 0 && (
        <div style={{ padding: '20px 16px 0' }}>
          <SectionHeader title="Accreditations" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {certificates.map((cert, i) => (
              <div
                key={i}
                style={{
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: 12,
                  padding: '12px 14px',
                  display: 'flex',
                  gap: 10,
                  alignItems: 'flex-start',
                }}
              >
                <div style={{ width: 28, height: 28, borderRadius: 8, background: '#DC262610', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <IconShield color="#DC2626" />
                </div>
                <div>
                  <p style={{ fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.text, margin: '0 0 2px' }}>{cert.name}</p>
                  <p style={{ fontFamily: T.font, fontSize: 11, color: T.textSec, margin: 0 }}>
                    {cert.issuedBy} · {cert.year}{cert.registrationNumber ? ` · Reg: ${cert.registrationNumber}` : ''}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Patient safety note */}
      <div style={{ padding: '20px 16px 0' }}>
        <div
          style={{
            background: '#FFF7ED',
            border: '1px solid #FED7AA',
            borderRadius: 12,
            padding: '14px 16px',
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
          }}
        >
          <div style={{ flexShrink: 0, marginTop: 1 }}><IconShield color="#EA580C" /></div>
          <div>
            <p style={{ fontFamily: T.font, fontSize: 12, fontWeight: 700, color: '#9A3412', margin: '0 0 3px' }}>Patient Safety</p>
            <p style={{ fontFamily: T.font, fontSize: 11, color: '#C2410C', margin: 0, lineHeight: 1.6 }}>
              {provider?.isVerified
                ? 'This hospital is verified on NeoHealthCard.'
                : 'This hospital is listed on NeoHealthCard but not yet verified.'} Your records are shared only with your consent. Please carry a valid photo ID and insurance card for your OPD visit.
            </p>
          </div>
        </div>
      </div>

      {/* NeoHealthCard branding */}
      <div style={{ padding: '20px 16px 0', textAlign: 'center' }}>
        <span style={{ fontFamily: T.font, fontSize: 9, color: T.textTer, lineHeight: 1.6 }}>
          NeoHealthCard™ · Records shared only with your consent
        </span>
      </div>

      {/* Book OPD Section */}
      <div style={{ padding: '24px 16px 0' }}>
        {!showBooking ? (
          <button
            onClick={() => setShowBooking(true)}
            style={{
              width: '100%',
              fontFamily: T.font,
              fontSize: 14,
              fontWeight: 700,
              color: T.white,
              background: '#DC2626',
              border: 'none',
              borderRadius: 14,
              padding: '15px 0',
              cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(220,38,38,0.3)',
            }}
          >
            Book OPD Appointment
          </button>
        ) : bookResult ? (
          /* Success */
          <div
            style={{
              background: T.white,
              border: `1px solid ${T.border}`,
              borderRadius: 16,
              padding: '24px 20px',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                background: '#ECFDF5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 14px',
              }}
            >
              <IconCheck color="#059669" />
            </div>
            <h3 style={{ fontFamily: T.font, fontSize: 18, fontWeight: 800, color: T.text, margin: '0 0 6px' }}>
              Appointment Requested
            </h3>
            <p style={{ fontFamily: T.font, fontSize: 13, color: T.textSec, margin: '0 0 16px' }}>
              Your OPD appointment at <strong>{provider.name}</strong> has been booked.
            </p>
            {bookResult.tokenNumber && (
              <div
                style={{
                  background: '#FEF2F2',
                  border: '1px solid #FCA5A5',
                  borderRadius: 12,
                  padding: '14px 16px',
                  marginBottom: 16,
                }}
              >
                <p style={{ fontFamily: T.font, fontSize: 11, color: T.textSec, margin: '0 0 4px' }}>Token Number</p>
                <p style={{ fontFamily: T.font, fontSize: 26, fontWeight: 900, color: '#DC2626', margin: 0, letterSpacing: '0.04em' }}>
                  #{bookResult.tokenNumber}
                </p>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <button
                onClick={() => navigate('/patient/appointments')}
                style={{
                  flex: 1,
                  fontFamily: T.font,
                  fontSize: 13,
                  fontWeight: 700,
                  color: T.white,
                  background: '#DC2626',
                  border: 'none',
                  borderRadius: 12,
                  padding: '12px 0',
                  cursor: 'pointer',
                }}
              >
                View Appointments
              </button>
              <button
                onClick={() => navigate('/patient/timeline')}
                style={{
                  flex: 1,
                  fontFamily: T.font,
                  fontSize: 13,
                  fontWeight: 700,
                  color: '#DC2626',
                  background: T.white,
                  border: `1.5px solid #DC2626`,
                  borderRadius: 12,
                  padding: '12px 0',
                  cursor: 'pointer',
                }}
              >
                Go to Timeline
              </button>
            </div>
            <button
              onClick={() => navigate('/patient/care-services/hospitals')}
              style={{
                width: '100%',
                fontFamily: T.font,
                fontSize: 12,
                fontWeight: 600,
                color: T.textSec,
                background: 'transparent',
                border: 'none',
                padding: '8px 0',
                cursor: 'pointer',
              }}
            >
              Back to Hospitals
            </button>
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <span style={{ fontFamily: T.font, fontSize: 9, color: T.textTer, lineHeight: 1.6 }}>
                NeoHealthCard™ · Booking recorded on your NeoHealthCard account · The hospital will confirm your slot
              </span>
            </div>
          </div>
        ) : (
          /* Booking form */
          <div
            style={{
              background: T.white,
              border: `1px solid ${T.border}`,
              borderRadius: 16,
              padding: '20px 16px',
              boxShadow: T.shadow,
            }}
          >
            <h3 style={{ fontFamily: T.font, fontSize: 15, fontWeight: 800, color: T.text, margin: '0 0 16px' }}>
              Book OPD Appointment
            </h3>

            {/* Department */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontFamily: T.font, fontSize: 12, fontWeight: 600, color: T.text, display: 'block', marginBottom: 6 }}>
                Department *
              </label>
              <select
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                style={{
                  width: '100%',
                  fontFamily: T.font,
                  fontSize: 13,
                  color: T.text,
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: 10,
                  padding: '10px 12px',
                  outline: 'none',
                  boxSizing: 'border-box',
                  appearance: 'none',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 12px center',
                  paddingRight: 32,
                }}
              >
                {provider.specialties?.length ? (
                  provider.specialties.map((spec) => (
                    <option key={spec} value={spec}>{spec}</option>
                  ))
                ) : (
                  <option value="General OPD">General OPD</option>
                )}
              </select>
            </div>

            {/* Date Picker (5-day grid) */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontFamily: T.font, fontSize: 12, fontWeight: 600, color: T.text, display: 'block', marginBottom: 6 }}>
                Select Date *
              </label>
              <div style={{ display: 'flex', gap: 6, overflowX: 'auto' }}>
                {datePicker.map((d) => (
                  <button
                    key={d.value}
                    onClick={() => { setSelectedDate(d.value); setSelectedSlot(''); }}
                    style={{
                      fontFamily: T.font,
                      fontSize: 11,
                      fontWeight: 600,
                      color: selectedDate === d.value ? T.white : T.textSec,
                      background: selectedDate === d.value ? '#DC2626' : T.white,
                      border: `1.5px solid ${selectedDate === d.value ? '#DC2626' : T.border}`,
                      borderRadius: 10,
                      padding: '8px 12px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      transition: 'background 0.15s, color 0.15s',
                    }}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Time Slot Grid */}
            {selectedDate && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontFamily: T.font, fontSize: 12, fontWeight: 600, color: T.text, display: 'block', marginBottom: 6 }}>
                  Preferred Time * (hospital confirms your slot)
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                  {timeSlots.map((time) => (
                    <button
                      key={time}
                      onClick={() => setSelectedSlot(time)}
                      style={{
                        fontFamily: T.font,
                        fontSize: 11,
                        fontWeight: 600,
                        color: selectedSlot === time ? T.white : T.text,
                        background: selectedSlot === time ? '#DC2626' : T.white,
                        border: `1px solid ${selectedSlot === time ? '#DC2626' : T.border}`,
                        borderRadius: 8,
                        padding: '7px 4px',
                        cursor: 'pointer',
                        transition: 'background 0.15s, color 0.15s',
                      }}
                    >
                      {formatSlotTime(time)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Reason */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontFamily: T.font, fontSize: 12, fontWeight: 600, color: T.text, display: 'block', marginBottom: 6 }}>
                Reason for Visit
              </label>
              <textarea
                rows={2}
                placeholder="Describe your symptoms or reason for visit..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                style={{
                  width: '100%',
                  fontFamily: T.font,
                  fontSize: 13,
                  color: T.text,
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: 10,
                  padding: '10px 12px',
                  outline: 'none',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Payment mode */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontFamily: T.font, fontSize: 12, fontWeight: 600, color: T.text, display: 'block', marginBottom: 8 }}>
                Payment
              </label>
              <div style={{ fontFamily: T.font, fontSize: 12, color: T.textSec, background: T.ice, border: `1px solid ${T.border}`, borderRadius: 10, padding: '10px 14px', lineHeight: 1.6 }}>
                Pay at the hospital desk — online payment is not available yet. Carry your insurance card if you plan to use insurance.
              </div>
            </div>

            {/* Consent */}
            <div style={{ marginBottom: 16 }}>
              <label
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'flex-start',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                  style={{ marginTop: 2, accentColor: '#DC2626', flexShrink: 0 }}
                />
                <span style={{ fontFamily: T.font, fontSize: 11, color: T.textSec, lineHeight: 1.6 }}>
                  I consent to sharing my health summary with this hospital for this consultation — a 72-hour consent grant is recorded on my NeoHealthCard account, revocable anytime from Consent &amp; Sharing.
                </span>
              </label>
            </div>

            {/* Error */}
            {bookError && (
              <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 10, padding: '10px 14px', marginBottom: 12 }}>
                <p style={{ fontFamily: T.font, fontSize: 12, color: '#B91C1C', margin: 0 }}>{bookError}</p>
              </div>
            )}

            {/* Buttons */}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => { setShowBooking(false); setBookError(null); }}
                style={{
                  flex: 1,
                  fontFamily: T.font,
                  fontSize: 13,
                  fontWeight: 600,
                  color: T.textSec,
                  background: T.white,
                  border: `1.5px solid ${T.border}`,
                  borderRadius: 12,
                  padding: '12px 0',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleBookOPD}
                disabled={booking}
                style={{
                  flex: 2,
                  fontFamily: T.font,
                  fontSize: 13,
                  fontWeight: 700,
                  color: T.white,
                  background: booking ? '#FCA5A5' : '#DC2626',
                  border: 'none',
                  borderRadius: 12,
                  padding: '12px 0',
                  cursor: booking ? 'not-allowed' : 'pointer',
                  transition: 'background 0.15s',
                }}
              >
                {booking ? 'Booking...' : 'Confirm Appointment'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
